import React, { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX } from "lucide-react";
// Hero Section with Video Background
const Hero = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // music toggle state

  const videoRef = useRef(null);

  // Autoplay with mute → enable audio after user interaction
  useEffect(() => {
    const enableAudio = () => {
      if (videoRef.current && !isMuted) {
        videoRef.current.muted = false;
        videoRef.current.volume = 1;
        videoRef.current.play().catch(() => {});
      }
    };

    window.addEventListener("click", enableAudio);
    window.addEventListener("touchstart", enableAudio);

    return () => {
      window.removeEventListener("click", enableAudio);
      window.removeEventListener("touchstart", enableAudio);
    };
  }, [isMuted]);

  // Toggle button handler
  const toggleMute = () => {
    if (!videoRef.current) return;

    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    videoRef.current.muted = newMuteState;

    if (!newMuteState) {
      videoRef.current.volume = 1;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <section
      id="home"
      className="relative h-screen w-full overflow-hidden flex items-end"
    >
      {/* Background Wrapper */}
      <div className="absolute inset-0 bg-neutral-950">
        {!videoError && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoError(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              videoLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            <source src="/assets/1205_optimized.mp4" type="video/mp4" />
          </video>
        )}

        {/* Fallback */}
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
            videoLoaded && !videoError ? "opacity-0" : "opacity-100"
          }`}
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80)",
          }}
        />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/60 via-transparent to-neutral-950/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/40 via-transparent to-neutral-950/40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_0%,rgba(0,0,0,0.6)_100%)]" />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' fill='white' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* CONTENT */}
      <div className="relative z-10 w-full flex flex-col items-center text-center px-6 pb-20 sm:pb-24">
        <p className="text-base sm:text-lg md:text-xl text-neutral-300 tracking-[0.15em] font-light animate-[fadeSlideUp_1s_ease_0.7s_both]">
          CAPTURING LOVE, LIFE & LUXURY
        </p>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-10 sm:gap-16 mt-4 animate-[fadeSlideUp_1s_ease_0.9s_both]">
          {[
            { value: "9+", label: "Years" },
            { value: "250+", label: "Weddings" },
            { value: "∞", label: "Memories" },
          ].map((stat) => (
            <div key={stat.label} className="text-center min-w-[80px]">
              <div className="text-3xl sm:text-4xl md:text-5xl text-white font-serif">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-neutral-400 tracking-[0.3em] mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 🎵 MUSIC TOGGLE BUTTON */}
      <button
        onClick={toggleMute}
        className="absolute bottom-6 right-6 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-xl flex items-center justify-center hover:bg-white/30 transition-all border border-white/40"
      >
        {isMuted ? (
          <VolumeX className="text-white w-6 h-6" />
        ) : (
          <Volume2 className="text-white w-6 h-6" />
        )}
      </button>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Hero;